import React, { useState, useRef } from "react";
import { Modal, Button, Upload, Table, message, Spin } from "antd";
import { InboxOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { CDN_IMAGE_BASE, getDeployEnv } from "../../config/mediaPaths";
import { GenerateShortID } from "./StoreUtils";
import styles from "./CatalogImport.module.css";

const { Dragger } = Upload;

const SAMPLE_FILE_URL = `${CDN_IMAGE_BASE}/MyHub/misc./public/${getDeployEnv()}/catalog-import-sample.xlsx`;

const ITEM_REQUIRED_COLS = ["ItemName", "BasePrice", "ItemType"];
const ITEM_ALLOWED_COLS = ["ItemName", "Description", "BasePrice", "ItemType", "Discount"];
const ITEM_TYPES = ["PRODUCT", "SERVICE"];
const CUST_REQUIRED_COLS = ["ItemName", "Name", "Type"];
const CUST_ALLOWED_COLS = ["ItemName", "Name", "Type", "Price", "AddonPrice", "DropdownOptions"];
const CUST_TYPES = ["boolean", "text", "file", "dropdown"];
const VAR_REQUIRED_COLS = ["ItemName", "VariantName", "Type", "OptionLabel"];
const VAR_ALLOWED_COLS = ["ItemName", "VariantName", "Type", "OptionLabel", "OptionPrice", "AddonPrice"];
const VAR_TYPES = ["single", "multiple"];

const CUSTOMIZATIONS_SHEET = "Customizations";
const VARIANTS_SHEET = "Variants";

const STATUS = {
  IDLE: "idle",
  PARSING: "parsing",
  PREVIEW: "preview",
  IMPORTING: "importing",
  DONE: "done",
};

const uid = () => `ci_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

function findDataRows(normalizedRows) {
  let separatorIndex = -1;
  for (let i = 0; i < normalizedRows.length; i++) {
    const firstVal = String(normalizedRows[i][Object.keys(normalizedRows[i])[0]] || "").trim();
    if (firstVal.includes("⬇") || firstVal.includes("⬆") || firstVal.toLowerCase().includes("enter your actual")) {
      separatorIndex = i;
      break;
    }
  }
  if (separatorIndex !== -1) {
    return normalizedRows.slice(separatorIndex + 1);
  }
  return normalizedRows.filter((row) => {
    const firstVal = String(row[Object.keys(row)[0]] || "").trim().toLowerCase();
    return !firstVal.includes("name") || !firstVal.includes("hint");
  });
}

function normalizeSheet(sheet) {
  const rawJson = XLSX.utils.sheet_to_json(sheet, { defval: "" });
  if (!rawJson || rawJson.length === 0) return [];
  return rawJson.map((row, idx) => {
    const normalized = {};
    Object.defineProperty(normalized, "_originalLine", {
      value: idx + 2,
      enumerable: false,
      writable: true,
      configurable: true,
    });
    for (const key of Object.keys(row)) {
      normalized[key.replace(/\*$/, "").trim()] = row[key];
    }
    return normalized;
  });
}

function validateCatalogRows(rows, existingItems) {
  const errors = [];
  const valid = [];
  const seenNames = new Set();

  rows.forEach((row, i) => {
    const line = row._originalLine || (i + 2);
    const rowErrors = [];

    const itemName = (row.ItemName || "").toString().trim();
    if (!itemName) {
      rowErrors.push(`Row ${line}: ItemName is required`);
    } else if (itemName.length > 100) {
      rowErrors.push(`Row ${line}: ItemName must be 100 characters or less`);
    } else if (seenNames.has(itemName.toLowerCase())) {
      rowErrors.push(`Row ${line}: ItemName "${itemName}" is duplicated in the file`);
    } else if (existingItems.some((ex) => (ex.ItemName || "").toLowerCase() === itemName.toLowerCase())) {
      rowErrors.push(`Row ${line}: ItemName "${itemName}" already exists in catalog`);
    } else {
      seenNames.add(itemName.toLowerCase());
    }

    const basePriceRaw = row.BasePrice;
    const basePrice = basePriceRaw !== undefined && basePriceRaw !== null && basePriceRaw !== ""
      ? Number(basePriceRaw)
      : NaN;
    if (isNaN(basePrice) || basePrice < 0) {
      rowErrors.push(`Row ${line}: BasePrice must be a number >= 0`);
    }

    const itemType = (row.ItemType || "").toString().trim().toUpperCase();
    if (!itemType) {
      rowErrors.push(`Row ${line}: ItemType is required`);
    } else if (!ITEM_TYPES.includes(itemType)) {
      rowErrors.push(`Row ${line}: ItemType must be "PRODUCT" or "SERVICE"`);
    }

    if (row.StockCount !== undefined && row.StockCount !== null && row.StockCount !== "") {
      rowErrors.push(`Row ${line}: StockCount field is no longer supported`);
    }

    if (row.Discount !== undefined && row.Discount !== null && row.Discount !== "") {
      const disc = Number(row.Discount);
      if (isNaN(disc) || disc < 0 || disc > 100) {
        rowErrors.push(`Row ${line}: Discount must be between 0 and 100`);
      }
    }

    if (row.Unit !== undefined && row.Unit !== null && row.Unit !== "") {
      rowErrors.push(`Row ${line}: Unit field is no longer supported`);
    }

    const desc = (row.Description || "").toString();
    if (desc.length > 200) {
      rowErrors.push(`Row ${line}: Description must be 200 characters or less`);
    }

    if (rowErrors.length > 0) {
      errors.push({ line, itemName: itemName || `Row ${line}`, errors: rowErrors, raw: { ...row } });
    } else {
      valid.push({
        _rowId: uid(),
        _line: line,
        ItemName: itemName,
        Description: desc,
        BasePrice: basePrice,
        ItemType: itemType,
        Discount: row.Discount !== undefined && row.Discount !== null && row.Discount !== ""
          ? Number(row.Discount) : 0,
      });
    }
  });

  return { valid, errors };
}

function validateCustomizations(rows, validItemNames) {
  const errors = [];
  const valid = [];
  const nameMap = {};

  rows.forEach((row, i) => {
    const line = row._originalLine || (i + 2);
    const rowErrors = [];

    const itemName = (row.ItemName || "").toString().trim();
    if (!itemName) {
      rowErrors.push(`Row ${line}: ItemName is required`);
    } else if (!validItemNames.has(itemName.toLowerCase())) {
      rowErrors.push(`Row ${line}: ItemName "${itemName}" does not match any item in Catalog Items`);
    }

    const custName = (row.Name || "").toString().trim();
    if (!custName) {
      rowErrors.push(`Row ${line}: Name is required`);
    }

    const custType = (row.Type || "").toString().trim().toLowerCase();
    if (!custType) {
      rowErrors.push(`Row ${line}: Type is required`);
    } else if (!CUST_TYPES.includes(custType)) {
      rowErrors.push(`Row ${line}: Type must be one of: ${CUST_TYPES.join(", ")}`);
    }

    if (row.Price !== undefined && row.Price !== null && row.Price !== "") {
      const p = Number(row.Price);
      if (isNaN(p) || p < 0) {
        rowErrors.push(`Row ${line}: Price must be a number >= 0`);
      }
    }

    const addonPriceRaw = (row.AddonPrice || "").toString().trim().toUpperCase();
    if (addonPriceRaw && addonPriceRaw !== "TRUE" && addonPriceRaw !== "FALSE") {
      rowErrors.push(`Row ${line}: AddonPrice must be TRUE or FALSE`);
    }

    if (custType === "dropdown") {
      const opts = (row.DropdownOptions || "").toString().trim();
      if (!opts) {
        rowErrors.push(`Row ${line}: DropdownOptions is required when Type is "dropdown"`);
      }
    }

    if (rowErrors.length > 0) {
      errors.push({ line, itemName: itemName || `Row ${line}`, errors: rowErrors, raw: { ...row } });
    } else {
      const key = `${itemName.toLowerCase()}|${custName.toLowerCase()}|${custType}`;
      if (nameMap[key]) {
        errors.push({ line, itemName: itemName || `Row ${line}`, errors: [`Row ${line}: Duplicate customisation "${custName}" (${custType}) for item "${itemName}"`], raw: { ...row } });
      } else {
        nameMap[key] = true;
        valid.push({
          _rowId: uid(),
          _line: line,
          ItemName: itemName,
          Name: custName,
          Type: custType,
          Price: row.Price !== undefined && row.Price !== null && row.Price !== "" ? Number(row.Price) : 0,
          AddonPrice: (row.AddonPrice || "").toString().trim().toUpperCase() !== "FALSE",
          DropdownOptions: custType === "dropdown" ? (row.DropdownOptions || "").toString().trim() : "",
        });
      }
    }
  });

  return { valid, errors };
}

function validateVariants(rows, validItemNames) {
  const errors = [];
  const valid = [];

  rows.forEach((row, i) => {
    const line = row._originalLine || (i + 2);
    const rowErrors = [];

    const itemName = (row.ItemName || "").toString().trim();
    if (!itemName) {
      rowErrors.push(`Row ${line}: ItemName is required`);
    } else if (!validItemNames.has(itemName.toLowerCase())) {
      rowErrors.push(`Row ${line}: ItemName "${itemName}" does not match any item in Catalog Items`);
    }

    const variantName = (row.VariantName || "").toString().trim();
    if (!variantName) {
      rowErrors.push(`Row ${line}: VariantName is required`);
    }

    const varType = (row.Type || "").toString().trim().toLowerCase();
    if (!varType) {
      rowErrors.push(`Row ${line}: Type is required`);
    } else if (!VAR_TYPES.includes(varType)) {
      rowErrors.push(`Row ${line}: Type must be "single" or "multiple"`);
    }

    const optionLabel = (row.OptionLabel || "").toString().trim();
    if (!optionLabel) {
      rowErrors.push(`Row ${line}: OptionLabel is required`);
    }

    const optionPriceRaw = row.OptionPrice !== undefined && row.OptionPrice !== null && row.OptionPrice !== ""
      ? Number(row.OptionPrice) : 0;
    if (isNaN(optionPriceRaw)) {
      rowErrors.push(`Row ${line}: OptionPrice must be a number`);
    }

    const addonPriceRaw = (row.AddonPrice || "").toString().trim().toUpperCase();
    if (addonPriceRaw && addonPriceRaw !== "TRUE" && addonPriceRaw !== "FALSE") {
      rowErrors.push(`Row ${line}: AddonPrice must be TRUE or FALSE`);
    }

    if (rowErrors.length > 0) {
      errors.push({ line, itemName: itemName || `Row ${line}`, errors: rowErrors, raw: { ...row } });
    } else {
      valid.push({
        _rowId: uid(),
        _line: line,
        ItemName: itemName,
        VariantName: variantName,
        Type: varType,
        OptionLabel: optionLabel,
        OptionPrice: optionPriceRaw,
        AddonPrice: (row.AddonPrice || "").toString().trim().toUpperCase() !== "FALSE",
      });
    }
  });

  return { valid, errors };
}

function mergeCustomizations(items, customizations) {
  if (!customizations.length) return items;
  const itemMap = {};
  items.forEach((item) => {
    itemMap[item.ItemName.toLowerCase()] = item;
  });
  customizations.forEach((c) => {
    const item = itemMap[c.ItemName.toLowerCase()];
    if (!item) return;
    if (!item.Customisations) item.Customisations = [];
    const existing = item.Customisations.find(
      (ex) => ex.name.toLowerCase() === c.Name.toLowerCase() && ex.type === c.Type
    );
    if (existing) return;
    const cust = {
      customisationId: GenerateShortID(),
      name: c.Name,
      type: c.Type,
      price: c.Price,
      addonPrice: c.AddonPrice,
      DropdownOptions: c.Type === "dropdown"
        ? c.DropdownOptions.split(",").map((label) => ({
            optionId: GenerateShortID(),
            label: label.trim(),
          })).filter((o) => o.label)
        : [],
    };
    item.Customisations.push(cust);
  });
  return items;
}

function mergeVariants(items, variants) {
  if (!variants.length) return items;
  const itemMap = {};
  items.forEach((item) => {
    itemMap[item.ItemName.toLowerCase()] = item;
  });
  const variantGroups = {};
  variants.forEach((v) => {
    const key = `${v.ItemName.toLowerCase()}|${v.VariantName.toLowerCase()}|${v.Type}`;
    if (!variantGroups[key]) {
      variantGroups[key] = {
        ItemName: v.ItemName,
        VariantName: v.VariantName,
        Type: v.Type,
        Options: [],
      };
    }
    variantGroups[key].Options.push({
      optionId: GenerateShortID(),
      label: v.OptionLabel,
      price: v.OptionPrice,
      addonPrice: v.AddonPrice,
    });
  });
  Object.values(variantGroups).forEach((group) => {
    const item = itemMap[group.ItemName.toLowerCase()];
    if (!item) return;
    if (!item.Variants) item.Variants = [];
    const existing = item.Variants.find(
      (ex) => ex.name.toLowerCase() === group.VariantName.toLowerCase() && ex.type === group.Type
    );
    if (existing) return;
    item.Variants.push({
      variantId: GenerateShortID(),
      name: group.VariantName,
      type: group.Type,
      Options: group.Options,
    });
  });
  return items;
}

function transformToHubItems(validRows, customizations, variants) {
  const items = validRows.map((row) => ({
    ItemId: GenerateShortID(6),
    ItemName: row.ItemName,
    Description: row.Description,
    BasePrice: row.BasePrice,
    ItemType: row.ItemType,
    Discount: row.Discount,
    Variants: [],
    Customisations: [],
    ItemImages: [],
  }));
  mergeCustomizations(items, customizations);
  mergeVariants(items, variants);
  return items;
}

const CatalogImport = ({ visible, onCancel, onImport, existingItems = [] }) => {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [parsedData, setParsedData] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setStatus(STATUS.IDLE);
    setParsedData(null);
    fileRef.current = null;
  };

  const handleClose = () => {
    reset();
    onCancel();
  };

  const handleDownloadSample = () => {
    window.open(SAMPLE_FILE_URL, "_blank", "noopener,noreferrer");
  };

  const handleFile = (file) => {
    fileRef.current = file;
    setStatus(STATUS.PARSING);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetNames = workbook.SheetNames;
        if (!sheetNames || sheetNames.length === 0) {
          message.error("The file contains no sheets");
          setStatus(STATUS.IDLE);
          return;
        }

        const catSheetName = sheetNames[0];
        const catSheet = workbook.Sheets[catSheetName];
        const catNormalized = normalizeSheet(catSheet);
        const catRows = findDataRows(catNormalized);

        if (catRows.length === 0) {
          message.error(`No valid data rows found in "${catSheetName}"`);
          setStatus(STATUS.IDLE);
          return;
        }

        const catHeaders = Object.keys(catRows[0]);
        const missingRequired = ITEM_REQUIRED_COLS.filter((c) => !catHeaders.includes(c));
        if (missingRequired.length > 0) {
          message.error(`Missing required columns in "${catSheetName}": ${missingRequired.join(", ")}`);
          setStatus(STATUS.IDLE);
          return;
        }

        const unknownCatCols = catHeaders.filter((c) => !ITEM_ALLOWED_COLS.includes(c));
        if (unknownCatCols.length > 0) {
          message.warning(`Unknown columns in "${catSheetName}" will be ignored: ${unknownCatCols.join(", ")}`);
        }

        const { valid: validItems, errors: itemErrors } = validateCatalogRows(catRows, existingItems);
        const validItemNames = new Set(validItems.map((r) => r.ItemName.toLowerCase()));

        let customizations = { valid: [], errors: [], total: 0 };
        let variants = { valid: [], errors: [], total: 0 };

        const custSheetIdx = sheetNames.findIndex((n) => n.trim() === CUSTOMIZATIONS_SHEET);
        if (custSheetIdx !== -1) {
          const custSheet = workbook.Sheets[sheetNames[custSheetIdx]];
          const custNormalized = normalizeSheet(custSheet);
          const custRows = findDataRows(custNormalized);
          if (custRows.length > 0) {
            const custHeaders = Object.keys(custRows[0]);
            const missingCust = CUST_REQUIRED_COLS.filter((c) => !custHeaders.includes(c));
            if (missingCust.length === 0) {
              const unknownCustCols = custHeaders.filter((c) => !CUST_ALLOWED_COLS.includes(c));
              if (unknownCustCols.length > 0) {
                message.warning(`Unknown columns in "${CUSTOMIZATIONS_SHEET}" will be ignored: ${unknownCustCols.join(", ")}`);
              }
              customizations = validateCustomizations(custRows, validItemNames);
              customizations.total = custRows.length;
            } else {
              message.warning(`"${CUSTOMIZATIONS_SHEET}" missing columns: ${missingCust.join(", ")} — skipping`);
            }
          }
        }

        const varSheetIdx = sheetNames.findIndex((n) => n.trim() === VARIANTS_SHEET);
        if (varSheetIdx !== -1) {
          const varSheet = workbook.Sheets[sheetNames[varSheetIdx]];
          const varNormalized = normalizeSheet(varSheet);
          const varRows = findDataRows(varNormalized);
          if (varRows.length > 0) {
            const varHeaders = Object.keys(varRows[0]);
            const missingVar = VAR_REQUIRED_COLS.filter((c) => !varHeaders.includes(c));
            if (missingVar.length === 0) {
              const unknownVarCols = varHeaders.filter((c) => !VAR_ALLOWED_COLS.includes(c));
              if (unknownVarCols.length > 0) {
                message.warning(`Unknown columns in "${VARIANTS_SHEET}" will be ignored: ${unknownVarCols.join(", ")}`);
              }
              variants = validateVariants(varRows, validItemNames);
              variants.total = varRows.length;
            } else {
              message.warning(`"${VARIANTS_SHEET}" missing columns: ${missingVar.join(", ")} — skipping`);
            }
          }
        }

        const totalErrors = itemErrors.length + customizations.errors.length + variants.errors.length;

        setParsedData({
          items: validItems,
          itemErrors,
          itemTotal: catRows.length,
          customizations,
          variants,
          totalErrors,
        });
        setStatus(STATUS.PREVIEW);
      } catch (err) {
        message.error("Failed to parse file. Make sure it's a valid .xlsx file.");
        setStatus(STATUS.IDLE);
      }
    };
    reader.onerror = () => {
      message.error("Failed to read file");
      setStatus(STATUS.IDLE);
    };
    reader.readAsArrayBuffer(file);

    return false;
  };

  const handleImport = () => {
    if (!parsedData || parsedData.items.length === 0) return;
    try {
      const hubItems = transformToHubItems(
        parsedData.items,
        parsedData.customizations.valid,
        parsedData.variants.valid,
      );
      onImport(hubItems);
      message.success(`${hubItems.length} item(s) imported successfully`);
      reset();
    } catch {
      message.error("Import failed");
    }
  };

  const previewColumns = [
    {
      title: "#",
      dataIndex: "_line",
      key: "_line",
      width: 50,
      render: (v) => <span style={{ color: "#94a3b8" }}>{v}</span>,
    },
    {
      title: "ItemName",
      dataIndex: "ItemName",
      key: "ItemName",
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "ItemType",
      key: "ItemType",
      width: 90,
      render: (v) => (
        <span className={v === "SERVICE" ? styles.tagService : styles.tagProduct}>{v}</span>
      ),
    },
    {
      title: "Price",
      dataIndex: "BasePrice",
      key: "BasePrice",
      width: 80,
      render: (v) => `₹${v}`,
    },
    {
      title: "Unit",
      dataIndex: "Unit",
      key: "Unit",
      width: 80,
    },
    {
      title: "Customisations",
      key: "customisations",
      width: 100,
      render: (_, record) => {
        const count = parsedData?.customizations?.valid?.filter(
          (c) => c.ItemName.toLowerCase() === record.ItemName.toLowerCase()
        ).length || 0;
        return count > 0 ? <span style={{ color: "#166534" }}>{count}</span> : <span style={{ color: "#94a3b8" }}>—</span>;
      },
    },
    {
      title: "Variants",
      key: "variants",
      width: 80,
      render: (_, record) => {
        const vNames = new Set(
          (parsedData?.variants?.valid || [])
            .filter((v) => v.ItemName.toLowerCase() === record.ItemName.toLowerCase())
            .map((v) => v.VariantName.toLowerCase())
        );
        return vNames.size > 0 ? <span style={{ color: "#166534" }}>{vNames.size}</span> : <span style={{ color: "#94a3b8" }}>—</span>;
      },
    },
  ];

  const errorColumns = [
    { title: "Row", dataIndex: "line", key: "line", width: 60 },
    { title: "Item", dataIndex: "itemName", key: "itemName", ellipsis: true },
    {
      title: "Errors",
      dataIndex: "errors",
      key: "errors",
      render: (errs) => errs.map((e, i) => <div key={i} className={styles.errorText}>{e}</div>),
    },
  ];

  const pd = parsedData;

  return (
    <Modal
      title="Import Catalog from Excel"
      open={visible}
      onCancel={handleClose}
      width={760}
      footer={null}
      destroyOnClose
    >
      {status === STATUS.IDLE && (
        <div className={styles.idle}>
          <p className={styles.helpText}>
            Upload an Excel file (.xlsx) with catalog items. Start by downloading the sample template.
          </p>
          <div className={styles.idleActions}>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadSample}>
              Download sample template
            </Button>
          </div>
          <div className={styles.uploadArea}>
            <Dragger
              accept=".xlsx,.xls"
              beforeUpload={handleFile}
              showUploadList={false}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Click or drag a file here</p>
              <p className="ant-upload-hint">Supports .xlsx files with Catalog Items, Customizations & Variants sheets</p>
            </Dragger>
          </div>
        </div>
      )}

      {status === STATUS.PARSING && (
        <div className={styles.center}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "#64748b" }}>Parsing file...</p>
        </div>
      )}

      {status === STATUS.PREVIEW && pd && (
        <div className={styles.preview}>
          <div className={styles.summary}>
            <span className={styles.summaryValid}>{pd.items.length} items valid</span>
            {pd.totalErrors > 0 && (
              <span className={styles.summaryError}>, {pd.totalErrors} error(s)</span>
            )}
            {pd.customizations.valid.length > 0 && (
              <span style={{ color: "#2563eb", marginLeft: 8 }}>
                · {pd.customizations.valid.length} customisations
              </span>
            )}
            {pd.variants.valid.length > 0 && (
              <span style={{ color: "#7c3aed", marginLeft: 8 }}>
                · {pd.variants.valid.length} variant options
              </span>
            )}
          </div>

          {(pd.itemErrors.length > 0 || pd.customizations.errors.length > 0 || pd.variants.errors.length > 0) && (
            <div className={styles.errorSection}>
              <div className={styles.errorSectionTitle}>Fix these errors in your file and re-upload</div>
              {renderErrorTable("Items", pd.itemErrors)}
              {pd.customizations.errors.length > 0 && renderErrorTable("Customisations", pd.customizations.errors)}
              {pd.variants.errors.length > 0 && renderErrorTable("Variants", pd.variants.errors)}
            </div>
          )}

          {pd.items.length > 0 && (
            <div className={styles.validSection}>
              <div className={styles.validSectionTitle}>Items to import ({pd.items.length})</div>
              <Table
                dataSource={pd.items}
                columns={previewColumns}
                rowKey="_rowId"
                pagination={false}
                size="small"
                scroll={{ x: "max-content" }}
              />
            </div>
          )}

          <div className={styles.previewActions}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleDownloadSample} icon={<DownloadOutlined />}>
              Download sample
            </Button>
            <Button
              type="primary"
              onClick={handleImport}
              disabled={pd.items.length === 0}
              loading={status === STATUS.IMPORTING}
            >
              Import {pd.items.length} item{pd.items.length !== 1 ? "s" : ""}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );

  function renderErrorTable(label, errors) {
    if (!errors.length) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#991b1b", marginBottom: 6 }}>{label}</div>
        <Table
          dataSource={errors}
          columns={errorColumns}
          rowKey="line"
          pagination={false}
          size="small"
        />
      </div>
    );
  }
};

export default CatalogImport;
